import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Feed from './feed/feed';
import Hashtags from './hashtags';
import { reloadAllGallery } from '../../actions/gallery/galleryHeight';
import { setProfileHeaderHeight } from '../../actions/headerActions';
import { loadApp } from '../../actions/appActions';
import { Spinner } from 'wix-ui-tpa/Spinner';
import { st, classes } from './widget.st.css';
import triggerEvent from '../../actions/services/biSiteLogger';
import biData from '../../bi/feedEvents.json';
import { stringifyErrMsg } from '../../actions/more/utils';
import { BUSINESS, PERSONAL } from '../../constants';

const AsyncProfileHeader = React.lazy(() =>
  import(
    /* webpackChunkName: "profileHeader" */ './header/ProfileHeader/ProfileHeader'
  ),
);

const AsyncEmptyState = React.lazy(() =>
  import(/* webpackChunkName: "emptyState" */ './emptyState/emptyState'),
);

const WidgetApp = ({
  isMobile,
  isLoading,
  loadApp,
  reloadAllGallery,
  mediasError,
  isEmptyAccount,
  isRtl,
  isDisplayProfileInfo,
  m_isDisplayProfileInfo,
  setHeaderHeight,
  isBusinessApp,
  isConnected,
  user,
  isDisplayHashtagsFeed,
  m_isDisplayHashtagsFeed,
  isDisplayImagesFeed,
  m_isDisplayImagesFeed,
  isV4Styles,
  isCacheError,
  cacheError,
  feedComponentPosition,
  hashtagComponentPosition,
  profileComponentPosition,
  m_feedComponentPosition,
  m_hashtagComponentPosition,
  m_profileComponentPosition,
}) => {
  const isDisplayFeed = isMobile ? m_isDisplayImagesFeed : isDisplayImagesFeed;
  const isDisplayHashtags = isMobile
    ? m_isDisplayHashtagsFeed
    : isDisplayHashtagsFeed;
  const isProperUserData =
    (user && Object.keys(user).length && !user.isCache) ||
    (user && Object.keys(user).length && user.isCache && isCacheError);

  const isHeaderDisplayed =
    (isMobile && m_isDisplayProfileInfo && isProperUserData) ||
    (!isMobile && isDisplayProfileInfo && isProperUserData);
  const [counterResize, setCounter] = useState(0);

  useEffect(() => {
    triggerEvent(biData.APP_LOADED_START);
    // Add label to fullstory, To do, after adding script to server
    window.Wix.Performance.applicationLoaded();
    loadApp();
    window.onresize = () => {
      setCounter((prev) => prev + 1);
      reloadAllGallery();
    };
  }, [loadApp, reloadAllGallery]);

  useEffect(() => {
    if (!isHeaderDisplayed || !isBusinessApp) {
      setHeaderHeight(0);
    }
    // return () => localStorage.removeItem('expandData');
  }, [isHeaderDisplayed, isBusinessApp]);

  useEffect(() => {
    if (isProperUserData) {
      if (isConnected) {
        biData.APP_LOADED.user_id = user.id;
        biData.APP_LOADED.type = isBusinessApp ? BUSINESS : PERSONAL;
        biData.APP_LOADED.account_type = user.account_type || 'BUSINESS';
        biData.APP_LOADED.app_type = isBusinessApp ? BUSINESS : PERSONAL;
        biData.APP_LOADED.followers_count = user.followers_count || 0;
        biData.APP_LOADED.source = 'API';
        biData.APP_LOADED.api_description = '';
        if (isCacheError) {
          biData.APP_LOADED.source = 'CACHE';
          biData.APP_LOADED.api_description = stringifyErrMsg(
            cacheError.message,
          );
        }
        triggerEvent(biData.APP_LOADED);
        window.Wix.Performance.applicationLoadingStep(40);
      }
    }
  }, [isConnected, user, isCacheError]);

  const widgetContent = [];
  const postsFeed = isEmptyAccount ? (
    <AsyncEmptyState isEmptyAccount isMobile={isMobile} />
  ) : (
    <Feed isMobile={isMobile} key="feed" counterResize={counterResize} />
  );

  const profileHeader =
    isHeaderDisplayed && isBusinessApp && isProperUserData ? (
      <AsyncProfileHeader
        setHeaderHeight={setHeaderHeight}
        key="header"
        isMobile={isMobile}
        isRtl={isRtl}
        isEditorX={window.Wix.Styles.getStyleParams().booleans.responsive}
      />
    ) : (
      <div key="noHeader" id="rootProfile" />
    );

  const profilePos = isMobile
    ? m_profileComponentPosition
    : profileComponentPosition;
  const feedPos = isMobile ? m_feedComponentPosition : feedComponentPosition;
  const hashtagPos = isMobile
    ? m_hashtagComponentPosition
    : hashtagComponentPosition;

  const hashtagFeed = React.useMemo(
    () =>
      isBusinessApp &&
      isProperUserData &&
      isDisplayHashtags && (
        <Hashtags key="hashtags" counterResize={counterResize} />
      ),
    [isDisplayHashtags, isProperUserData, isBusinessApp],
  );

  widgetContent[profilePos] = profileHeader;
  widgetContent[hashtagPos] = hashtagFeed ? hashtagFeed : null;
  widgetContent[feedPos] = isDisplayFeed ? postsFeed : null;

  if (isLoading) {
    return (
      <div className={st(classes.loader, { isMobile })}>
        <Spinner diameter={isMobile ? 25 : 50} isCentered />
      </div>
    );
  }

  if (mediasError) {
    return <AsyncEmptyState error={mediasError} isMobile={isMobile} />;
  }

  return (
    <div className={st(classes.root, { isV4Styles, isMobile })}>
      {widgetContent}
    </div>
  );
};

const mapStateToProps = (state) => ({
  mediasError: state.appReducer.mediasError,
  isLoading: state.appReducer.isLoading,
  isMobile: state.appReducer.isMobile,
  isEmptyAccount: state.appReducer.isEmptyAccount,
  isDisplayProfileInfo: state.settingsReducer.isDisplayProfileInfo,
  m_isDisplayProfileInfo: state.settingsReducer.m_isDisplayProfileInfo,
  isBusinessApp: state.appReducer.isBusinessApp,
  isConnected: state.appReducer.isConnected,
  user: state.appReducer.user,
  isCacheError: state.appReducer.isCacheError,
  cacheError: state.appReducer.cacheError,
  isDisplayHashtagsFeed: state.settingsReducer.isDisplayHashtagsFeed,
  m_isDisplayHashtagsFeed: state.settingsReducer.m_isDisplayHashtagsFeed,
  isDisplayImagesFeed: state.settingsReducer.isDisplayImagesFeed,
  m_isDisplayImagesFeed: state.settingsReducer.m_isDisplayImagesFeed,
  isV4Styles: state.settingsReducer.isV4Styles,
  feedComponentPosition: state.settingsReducer.feedComponentPosition,
  hashtagComponentPosition: state.settingsReducer.hashtagComponentPosition,
  profileComponentPosition: state.settingsReducer.profileComponentPosition,
  m_feedComponentPosition: state.settingsReducer.m_feedComponentPosition,
  m_hashtagComponentPosition: state.settingsReducer.m_hashtagComponentPosition,
  m_profileComponentPosition: state.settingsReducer.m_profileComponentPosition,
});

const mapDispatchToProps = (dispatch) => ({
  reloadAllGallery: () => dispatch(reloadAllGallery()),
  loadApp: () => dispatch(loadApp()),
  setHeaderHeight: (height) => dispatch(setProfileHeaderHeight(height)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WidgetApp);
